import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ordersAPI, razorpayAPI } from '../utils/api';
import cartStore from '../utils/cartStore';
import { useLanguage } from '../context/LanguageContext';
import { useCurrency } from '../context/CurrencyContext';
import { toast } from 'sonner';
import { CreditCard, Tag, Truck, Package } from 'lucide-react';

const INTL_SHIPPING_INR = 3500;
const COUPON_CODE = 'EV1500';
const COUPON_MIN_ORDER = 15000;
const COUPON_DISCOUNT = 1500;
const COD_ADVANCE = 500;

const Checkout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const { formatPrice, currency } = useCurrency();
  const [placing, setPlacing] = useState(false);
  const [items, setItems] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState('Razorpay');
  const [couponInput, setCouponInput] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [form, setForm] = useState({
    name: '', phone: '', email: '', address: '', city: '', state: '', pincode: '', country: 'India', order_notes: '',
  });

  useEffect(() => {
    if (location.state?.buyNowItem) {
      const item = location.state.buyNowItem;
      setItems([{ id: 'buynow', product_id: item.product.id, product: item.product, quantity: item.quantity, color: item.color, size: item.size }]);
    } else {
      const cartItems = cartStore.getCart();
      if (cartItems.length === 0) { toast.error(t('cartEmpty')); navigate('/cart'); return; }
      setItems(cartItems);
    }
  }, [location.state, navigate, t]);

  const subtotal = useMemo(() => items.reduce((sum, item) => {
    const price = item.product?.discount_price || item.product?.price || 0;
    return sum + price * item.quantity;
  }, 0), [items]);

  const isInternational = form.country !== 'India';
  const shippingCharge = isInternational ? INTL_SHIPPING_INR : 0;
  const couponDiscount = appliedCoupon ? COUPON_DISCOUNT : 0;
  const grandTotal = subtotal + shippingCharge - couponDiscount;

  // COD: only available if total > 500
  const codAvailable = grandTotal > COD_ADVANCE;
  const isCOD = paymentMethod === 'COD';
  const advancePaid = isCOD ? COD_ADVANCE : grandTotal;
  const balanceDue = isCOD ? grandTotal - COD_ADVANCE : 0;

  // If COD selected but total <= 500, force switch to Razorpay
  useEffect(() => {
    if (isCOD && !codAvailable) setPaymentMethod('Razorpay');
  }, [grandTotal, isCOD, codAvailable]);

  const handleApplyCoupon = () => {
    if (couponInput.trim().toUpperCase() === COUPON_CODE) {
      if (subtotal >= COUPON_MIN_ORDER) {
        setAppliedCoupon(COUPON_CODE);
        toast.success(`Coupon ${COUPON_CODE} applied! ₹${COUPON_DISCOUNT.toLocaleString('en-IN')} off`);
      } else {
        toast.error(`Minimum order of ₹${COUPON_MIN_ORDER.toLocaleString('en-IN')} required for this coupon`);
      }
    } else {
      toast.error('Invalid coupon code');
    }
  };

  const removeCoupon = () => { setAppliedCoupon(null); setCouponInput(''); toast.success('Coupon removed'); };

  const validateForm = () => {
    const { name, phone, email, address, city, state, pincode, country } = form;
    if (!name || !phone || !email || !address || !city || !state || !pincode || !country) { toast.error('Please fill all required fields'); return false; }
    if (!/^\d{7,15}$/.test(phone.replace(/[\s-+]/g, ''))) { toast.error('Please enter a valid phone number'); return false; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { toast.error('Please enter a valid email address'); return false; }
    return true;
  };

  const createOrderInDB = async (payMethod, rpPaymentId = null) => {
    const orderItems = items.map(item => ({
      product_id: item.product?.id || item.product_id,
      product_name: item.product?.name || '',
      quantity: item.quantity,
      price: item.product?.discount_price || item.product?.price || 0,
      color: item.color || null, size: item.size || null,
    }));
    const { data } = await ordersAPI.create({
      customer_name: form.name, customer_email: form.email, customer_phone: form.phone,
      customer_address: `${form.address}, ${form.city}, ${form.state} - ${form.pincode}, ${form.country}`,
      items: orderItems, total: grandTotal, payment_method: payMethod,
      advance_paid: payMethod === 'COD' ? COD_ADVANCE : grandTotal,
      balance_due: payMethod === 'COD' ? grandTotal - COD_ADVANCE : 0,
      order_notes: [
        form.order_notes,
        isInternational ? `Intl shipping: ₹${INTL_SHIPPING_INR}` : null,
        appliedCoupon ? `Coupon ${appliedCoupon}: -₹${COUPON_DISCOUNT}` : null,
        payMethod === 'COD' ? `COD advance ₹${COD_ADVANCE} paid online, balance ₹${grandTotal - COD_ADVANCE} on delivery` : null,
        rpPaymentId ? `Payment ID: ${rpPaymentId}` : null,
      ].filter(Boolean).join(' | ') || null,
    });
    return data;
  };

  const openWhatsAppConfirmation = (order) => {
    const whatsappNumber = process.env.REACT_APP_WHATSAPP_NUMBER || '919999633121';
    const message = encodeURIComponent(`Hi Studio Crochet, I have placed an order on your website.\nOrder ID: ${order.id}\nName: ${form.name}\nPlease share tracking details.`);
    window.open(`https://wa.me/${whatsappNumber}?text=${message}`, '_blank');
  };

  const loadRazorpayScript = () => new Promise((resolve) => {
    if (document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]')) { resolve(true); return; }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

  const processRazorpayPayment = async (amountINR, description, onSuccess) => {
    const loaded = await loadRazorpayScript();
    if (!loaded) { toast.error('Failed to load payment gateway.'); setPlacing(false); return; }

    try {
      const totalPaise = Math.round(amountINR * 100);
      const { data: rpOrder } = await razorpayAPI.createOrder(totalPaise);
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: totalPaise, currency: 'INR',
        name: 'Studio Crochet', description,
        order_id: rpOrder.id,
        handler: async function (response) {
          try {
            await razorpayAPI.verifyPayment({ razorpay_order_id: response.razorpay_order_id, razorpay_payment_id: response.razorpay_payment_id, razorpay_signature: response.razorpay_signature });
            await onSuccess(response.razorpay_payment_id);
          } catch (_) { toast.error('Payment verification failed. Contact support.'); setPlacing(false); }
        },
        prefill: { name: form.name, email: form.email, contact: form.phone },
        theme: { color: '#C29B57' },
        modal: { ondismiss: function () { toast.error('Payment cancelled'); setPlacing(false); } },
      };
      const rp = new window.Razorpay(options);
      rp.on('payment.failed', function () { toast.error('Payment failed. Please try again.'); setPlacing(false); });
      rp.open();
    } catch (_) { toast.error('Failed to initiate payment.'); setPlacing(false); }
  };

  const handlePlaceOrder = async () => {
    if (!validateForm()) return;
    setPlacing(true);

    if (isCOD) {
      // COD: charge ₹500 advance via Razorpay, then create order
      await processRazorpayPayment(COD_ADVANCE, `COD Advance - ₹${COD_ADVANCE}`, async (paymentId) => {
        const order = await createOrderInDB('COD', paymentId);
        if (!location.state?.buyNowItem) cartStore.clearCart();
        openWhatsAppConfirmation(order);
        navigate('/order-confirmation', { state: { order, paymentMethod: 'COD', paymentId, advancePaid: COD_ADVANCE, balanceDue: grandTotal - COD_ADVANCE } });
      });
    } else {
      // Full prepaid via Razorpay
      await processRazorpayPayment(grandTotal, 'Order Payment', async (paymentId) => {
        const order = await createOrderInDB('Razorpay', paymentId);
        if (!location.state?.buyNowItem) cartStore.clearCart();
        openWhatsAppConfirmation(order);
        navigate('/order-confirmation', { state: { order, paymentMethod: 'Razorpay', paymentId } });
      });
    }
  };

  if (items.length === 0) return <div className="min-h-screen pt-24 pb-16 flex items-center justify-center"><p className="font-outfit text-[#8C8577]">{t('loading')}</p></div>;

  return (
    <div className="min-h-screen pt-24 pb-16 bg-[#FCFBF8]" data-testid="checkout-page">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="font-cormorant text-4xl sm:text-5xl mb-8" data-testid="checkout-heading">{t('checkout')}</h1>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Billing Details */}
          <div className="lg:col-span-2 space-y-6" data-testid="billing-form">
            <h2 className="font-cormorant text-2xl mb-4">{t('billingDetails')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="font-outfit text-sm uppercase tracking-wider text-[#1A1A18] block mb-2">{t('fullName')} *</label>
                <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-[#EBE4D5] bg-white rounded-md px-4 py-3 focus:border-[#C29B57] font-outfit text-base outline-none" data-testid="checkout-name" />
              </div>
              <div>
                <label className="font-outfit text-sm uppercase tracking-wider text-[#1A1A18] block mb-2">{t('phone')} *</label>
                <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full border border-[#EBE4D5] bg-white rounded-md px-4 py-3 focus:border-[#C29B57] font-outfit text-base outline-none" data-testid="checkout-phone" />
              </div>
            </div>
            <div>
              <label className="font-outfit text-sm uppercase tracking-wider text-[#1A1A18] block mb-2">{t('email')} *</label>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border border-[#EBE4D5] bg-white rounded-md px-4 py-3 focus:border-[#C29B57] font-outfit text-base outline-none" data-testid="checkout-email" />
            </div>
            <div>
              <label className="font-outfit text-sm uppercase tracking-wider text-[#1A1A18] block mb-2">Country *</label>
              <select value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="w-full border border-[#EBE4D5] bg-white rounded-md px-4 py-3 focus:border-[#C29B57] font-outfit text-base outline-none" data-testid="checkout-country">
                <option value="India">India</option><option value="UAE">UAE</option><option value="Saudi Arabia">Saudi Arabia</option><option value="Qatar">Qatar</option><option value="Kuwait">Kuwait</option><option value="Bahrain">Bahrain</option><option value="Oman">Oman</option><option value="United States">United States</option><option value="United Kingdom">United Kingdom</option><option value="Canada">Canada</option><option value="Australia">Australia</option><option value="Singapore">Singapore</option><option value="Malaysia">Malaysia</option><option value="Other">Other</option>
              </select>
              {isInternational && (
                <div className="mt-2 flex items-center gap-2 p-3 bg-[#C29B57]/10 border border-[#C29B57]/30 rounded-md" data-testid="intl-shipping-notice">
                  <Truck className="w-4 h-4 text-[#C29B57] flex-shrink-0" />
                  <p className="font-outfit text-sm text-[#4A463F]">International shipping: <span className="font-semibold text-[#C29B57]">{formatPrice(INTL_SHIPPING_INR)}</span></p>
                </div>
              )}
            </div>
            <div>
              <label className="font-outfit text-sm uppercase tracking-wider text-[#1A1A18] block mb-2">{t('shippingAddress')} *</label>
              <textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} rows="3" className="w-full border border-[#EBE4D5] bg-white rounded-md px-4 py-3 focus:border-[#C29B57] font-outfit text-base outline-none resize-none" data-testid="checkout-address" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div><label className="font-outfit text-sm uppercase tracking-wider text-[#1A1A18] block mb-2">{t('city')} *</label><input type="text" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="w-full border border-[#EBE4D5] bg-white rounded-md px-4 py-3 focus:border-[#C29B57] font-outfit text-base outline-none" data-testid="checkout-city" /></div>
              <div><label className="font-outfit text-sm uppercase tracking-wider text-[#1A1A18] block mb-2">{t('state')} *</label><input type="text" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="w-full border border-[#EBE4D5] bg-white rounded-md px-4 py-3 focus:border-[#C29B57] font-outfit text-base outline-none" data-testid="checkout-state" /></div>
              <div><label className="font-outfit text-sm uppercase tracking-wider text-[#1A1A18] block mb-2">{t('pincode')} *</label><input type="text" value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} className="w-full border border-[#EBE4D5] bg-white rounded-md px-4 py-3 focus:border-[#C29B57] font-outfit text-base outline-none" data-testid="checkout-pincode" /></div>
            </div>
            <div><label className="font-outfit text-sm uppercase tracking-wider text-[#1A1A18] block mb-2">{t('orderNotes')}</label><textarea value={form.order_notes} onChange={(e) => setForm({ ...form, order_notes: e.target.value })} rows="2" placeholder={t('orderNotesPlaceholder')} className="w-full border border-[#EBE4D5] bg-white rounded-md px-4 py-3 focus:border-[#C29B57] font-outfit text-base outline-none resize-none placeholder:text-[#8C8577]" data-testid="checkout-notes" /></div>

            {/* Coupon Code */}
            <div className="mt-6">
              <h2 className="font-cormorant text-2xl mb-4">Coupon Code</h2>
              {appliedCoupon ? (
                <div className="flex items-center justify-between p-4 border border-[#3A5A40] bg-[#3A5A40]/5 rounded-md" data-testid="coupon-applied">
                  <div className="flex items-center gap-3"><Tag className="w-5 h-5 text-[#3A5A40]" /><div><p className="font-outfit font-medium text-[#3A5A40]">{appliedCoupon} applied</p><p className="font-outfit text-sm text-[#8C8577]">-{formatPrice(COUPON_DISCOUNT)} discount</p></div></div>
                  <button onClick={removeCoupon} className="font-outfit text-sm text-[#9E2A2B] hover:underline" data-testid="remove-coupon">Remove</button>
                </div>
              ) : (
                <div className="flex gap-3" data-testid="coupon-input-section">
                  <input type="text" value={couponInput} onChange={(e) => setCouponInput(e.target.value)} placeholder="Enter coupon code" className="flex-1 border border-[#EBE4D5] bg-white rounded-md px-4 py-3 focus:border-[#C29B57] font-outfit text-base outline-none uppercase" data-testid="coupon-input" />
                  <button onClick={handleApplyCoupon} className="bg-[#1A1A18] text-white hover:bg-[#C29B57] transition-colors px-6 py-3 font-outfit text-sm uppercase tracking-wider rounded-md" data-testid="apply-coupon-button">Apply</button>
                </div>
              )}
            </div>

            {/* Payment Method */}
            <div className="mt-8">
              <h2 className="font-cormorant text-2xl mb-4">{t('paymentMethod')}</h2>
              <div className="space-y-3">
                {/* Prepaid */}
                <label className={`flex items-center gap-4 p-4 border rounded-md cursor-pointer transition-colors ${paymentMethod === 'Razorpay' ? 'border-[#C29B57] bg-[#C29B57]/5' : 'border-[#EBE4D5]'}`} data-testid="payment-razorpay">
                  <input type="radio" name="payment" value="Razorpay" checked={paymentMethod === 'Razorpay'} onChange={() => setPaymentMethod('Razorpay')} className="accent-[#C29B57]" />
                  <CreditCard className="w-5 h-5 text-[#C29B57]" />
                  <div>
                    <p className="font-outfit font-medium">{t('payOnline')}</p>
                    <p className="font-outfit text-sm text-[#8C8577]">{t('payOnlineDesc')}</p>
                  </div>
                </label>

                {/* COD */}
                <label className={`flex items-center gap-4 p-4 border rounded-md transition-colors ${!codAvailable ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'} ${paymentMethod === 'COD' ? 'border-[#C29B57] bg-[#C29B57]/5' : 'border-[#EBE4D5]'}`} data-testid="payment-cod">
                  <input type="radio" name="payment" value="COD" checked={paymentMethod === 'COD'} onChange={() => codAvailable && setPaymentMethod('COD')} disabled={!codAvailable} className="accent-[#C29B57]" />
                  <Package className="w-5 h-5 text-[#C29B57]" />
                  <div>
                    <p className="font-outfit font-medium">Cash on Delivery</p>
                    <p className="font-outfit text-sm text-[#8C8577]">
                      {codAvailable
                        ? `Pay ₹${COD_ADVANCE} advance online, rest on delivery`
                        : `Not available for orders ₹${COD_ADVANCE} or less`}
                    </p>
                  </div>
                </label>
              </div>

              {/* COD Advance Info Box */}
              {isCOD && (
                <div className="mt-4 p-4 bg-[#F3EFE6] border border-[#EBE4D5] rounded-md" data-testid="cod-advance-info">
                  <p className="font-outfit text-sm font-medium text-[#1A1A18] mb-2">Cash on Delivery - Payment Breakdown</p>
                  <div className="space-y-1.5">
                    <div className="flex justify-between font-outfit text-sm"><span>Total Order Amount</span><span>{formatPrice(grandTotal)}</span></div>
                    <div className="flex justify-between font-outfit text-sm text-[#C29B57] font-medium"><span>Advance Payment (Online Now)</span><span>{formatPrice(COD_ADVANCE)}</span></div>
                    <div className="flex justify-between font-outfit text-sm"><span>Remaining on Delivery</span><span>{formatPrice(balanceDue)}</span></div>
                  </div>
                  <p className="font-outfit text-xs text-[#8C8577] mt-3">Pay ₹{COD_ADVANCE} online now to confirm your COD order. Pay the remaining balance when your order is delivered.</p>
                </div>
              )}

              {currency !== 'INR' && (
                <p className="font-outfit text-xs text-[#8C8577] mt-2" data-testid="inr-notice">
                  {t('paymentInINR').replace('{amount}', (isCOD ? COD_ADVANCE : grandTotal).toLocaleString('en-IN'))}
                </p>
              )}
            </div>
          </div>

          {/* Order Summary */}
          <div className="bg-white p-6 rounded-lg h-fit sticky top-24" data-testid="order-summary">
            <h2 className="font-cormorant text-2xl mb-4">{t('orderSummary')}</h2>
            <div className="space-y-4 mb-6 max-h-80 overflow-y-auto">
              {items.map((item, idx) => {
                const price = item.product?.discount_price || item.product?.price || 0;
                return (
                  <div key={item.id || idx} className="flex gap-3 pb-4 border-b border-[#EBE4D5]" data-testid={`order-item-${idx}`}>
                    <div className="w-16 h-16 bg-[#F3EFE6] rounded overflow-hidden flex-shrink-0">
                      {item.product?.images?.length > 0 && <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${item.product.images[0]}`} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-outfit text-sm truncate">{item.product?.name}</p>
                      {item.color && <p className="font-outfit text-xs text-[#8C8577]">Color: {item.color}</p>}
                      {item.size && <p className="font-outfit text-xs text-[#8C8577]">Size: {item.size}</p>}
                      <p className="font-outfit text-sm">Qty: {item.quantity} x {formatPrice(price)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="space-y-3 mb-6">
              <div className="flex justify-between font-outfit text-sm"><span>{t('subtotal')}</span><span>{formatPrice(subtotal)}</span></div>
              <div className="flex justify-between font-outfit text-sm">
                <span>{t('shipping')}</span>
                {isInternational ? <span className="text-[#C29B57] font-medium" data-testid="shipping-charge">{formatPrice(INTL_SHIPPING_INR)}</span> : <span className="text-[#3A5A40]">{t('free')}</span>}
              </div>
              {appliedCoupon && (
                <div className="flex justify-between font-outfit text-sm text-[#3A5A40]" data-testid="coupon-discount-line"><span>Coupon ({appliedCoupon})</span><span>-{formatPrice(COUPON_DISCOUNT)}</span></div>
              )}
              <div className="border-t border-[#EBE4D5] pt-3 flex justify-between font-outfit text-lg">
                <span>{t('total')}</span>
                <span className="text-[#C29B57]" data-testid="checkout-total">{formatPrice(grandTotal)}</span>
              </div>
              {isCOD && (
                <>
                  <div className="flex justify-between font-outfit text-sm text-[#C29B57] font-medium" data-testid="cod-advance-line">
                    <span>Pay Now (Advance)</span><span>{formatPrice(COD_ADVANCE)}</span>
                  </div>
                  <div className="flex justify-between font-outfit text-sm text-[#8C8577]" data-testid="cod-balance-line">
                    <span>Pay on Delivery</span><span>{formatPrice(balanceDue)}</span>
                  </div>
                </>
              )}
            </div>
            <div className="border border-[#9E2A2B]/30 bg-[#9E2A2B]/5 rounded-md p-4 mb-4" data-testid="no-return-notice">
              <p className="font-outfit text-sm text-[#9E2A2B] font-medium text-center">Please Note: All products are non-returnable. No returns will be accepted after the order is placed.</p>
            </div>
            <button onClick={handlePlaceOrder} disabled={placing} className="w-full bg-[#1A1A18] text-white hover:bg-[#C29B57] transition-colors duration-300 rounded-none px-8 py-4 font-outfit uppercase tracking-wider text-sm disabled:opacity-50" data-testid="place-order-button">
              {placing ? t('processing') : isCOD ? `Pay ₹${COD_ADVANCE} & Confirm COD Order` : t('payNow')}
            </button>
            <p className="text-xs text-[#8C8577] text-center mt-4 font-outfit">{t('whatsappNote')}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
